/**
 * Password Change Dialog Component
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 5.1, 5.2, 5.5
 * 
 * Modal dialog for admin users to change other users' passwords
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { validatePasswordForm } from '../utils/passwordValidation';

export interface PasswordChangeDialogUser {
  id: string;
  username: string;
  email: string;
  name: string;
}

export interface PasswordChangeDialogProps {
  open: boolean;
  onClose: () => void;
  user: PasswordChangeDialogUser | null;
  onSubmit: (userId: string, newPassword: string, forcePasswordReset: boolean) => Promise<void>;
}

export const PasswordChangeDialog: React.FC<PasswordChangeDialogProps> = ({
  open,
  onClose,
  user,
  onSubmit,
}) => {
  // Form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forcePasswordReset, setForcePasswordReset] = useState(true); // Requirement 1.6: default checked
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setNewPassword('');
      setConfirmPassword('');
      setForcePasswordReset(true);
      setShowPassword(false);
      setShowConfirmPassword(false);
      setError(null);
    }
  }, [open]);

  // Validation
  const validation = validatePasswordForm(newPassword, confirmPassword);
  const isFormValid = validation.isValid && newPassword.length > 0;

  const handleSubmit = async () => {
    if (!user || !isFormValid) return;

    setIsLoading(true);
    setError(null);

    try {
      await onSubmit(user.id, newPassword, forcePasswordReset);
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'パスワードの変更に失敗しました';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="password-change-dialog-title"
    >
      <DialogTitle id="password-change-dialog-title">
        パスワード変更
      </DialogTitle>
      
      <DialogContent>
        {/* User info - Requirement 1.1 */}
        {user && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              対象ユーザー
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {user.name} ({user.username})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        )}

        {/* Error display - Requirement 5.4 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* New password field - Requirement 1.2 */}
        <TextField
          fullWidth
          label="新しいパスワード"
          type={showPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={isLoading}
          error={newPassword.length > 0 && validation.passwordErrors.length > 0}
          helperText={
            newPassword.length > 0 && validation.passwordErrors.length > 0
              ? validation.passwordErrors[0]
              : '8文字以上、大文字・小文字・数字・特殊文字を含む'
          }
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {/* Confirm password field - Requirement 1.3 */}
        <TextField
          fullWidth
          label="新しいパスワード（確認）"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
          error={confirmPassword.length > 0 && !!validation.confirmError}
          helperText={confirmPassword.length > 0 ? validation.confirmError : ''}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    aria-label={showConfirmPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {/* Force password reset checkbox - Requirement 1.4, 1.6 */}
        <FormControlLabel
          control={
            <Checkbox
              checked={forcePasswordReset}
              onChange={(e) => setForcePasswordReset(e.target.checked)}
              disabled={isLoading}
            />
          }
          label="次回ログイン時にパスワード変更を強制する"
        />
      </DialogContent>

      <DialogActions>
        {/* Cancel button - Requirement 1.5 */}
        <Button 
          onClick={handleClose} 
          disabled={isLoading}
        >
          キャンセル
        </Button>
        
        {/* Submit button - Requirement 1.5, 5.1, 5.2 */}
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? '変更中...' : 'パスワードを変更'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
